import { useEffect, useState } from "react";
import { PhoneCall, Download, Trash2, RefreshCw, User, Bike, Clock, FileAudio } from "lucide-react";
import api from "@/api/client";
import { toast } from "sonner";

function formatDuration(sec) {
  const m = Math.floor((sec || 0) / 60);
  const s = (sec || 0) % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatDate(iso) {
  try { return new Date(iso).toLocaleString("ar-LY"); } catch { return iso; }
}

function formatSize(bytes) {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Admin-only page to review recorded voice calls (silent recording, dispute review). */
export default function AdminCallRecordings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audioUrls, setAudioUrls] = useState({}); // {id: objectUrl}

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/call-recordings");
      setItems(data || []);
    } catch (e) {
      toast.error("تعذر تحميل التسجيلات");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  // Object URL cleanup
  useEffect(() => () => {
    Object.values(audioUrls).forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* ignore */ } });
  }, [audioUrls]);

  const loadAudio = async (rec) => {
    if (audioUrls[rec.id]) return;
    try {
      const res = await api.get(`/admin/call-recordings/${rec.id}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setAudioUrls((u) => ({ ...u, [rec.id]: url }));
    } catch { toast.error("تعذر تشغيل التسجيل"); }
  };

  const download = async (rec) => {
    try {
      const res = await api.get(`/admin/call-recordings/${rec.id}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = rec.file_name || `${rec.id}.webm`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch { toast.error("فشل التحميل"); }
  };

  const remove = async (rec) => {
    if (!window.confirm("حذف هذا التسجيل نهائياً؟")) return;
    try {
      await api.delete(`/admin/call-recordings/${rec.id}`);
      setItems((s) => s.filter((x) => x.id !== rec.id));
      toast.success("تم الحذف");
    } catch { toast.error("فشل الحذف"); }
  };

  return (
    <div className="min-h-screen bg-[var(--background)] pb-8" data-testid="admin-call-recordings-page" dir="rtl">
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-black flex items-center gap-2" style={{ fontFamily: "Cairo" }}>
              <FileAudio className="text-primary-brand"/> سجلات المكالمات
            </h1>
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              تسجيلات صوتية صامتة لمكالمات الطلبات بين الزبائن والسائقين — للمراجعة الإدارية فقط
            </p>
          </div>
          <button
            onClick={refresh}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-[var(--border)] text-sm font-bold hover:bg-[var(--surface-alt)]"
            data-testid="refresh-recordings"
          >
            <RefreshCw size={16}/> تحديث
          </button>
        </div>

        {loading ? (
          <div className="bg-white rounded-3xl p-12 text-center text-[var(--text-secondary)]">جاري التحميل...</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center" data-testid="no-recordings">
            <FileAudio size={40} className="mx-auto text-[var(--text-secondary)] mb-3"/>
            <div className="font-bold">لا توجد تسجيلات بعد</div>
            <div className="text-xs text-[var(--text-secondary)] mt-1">
              يتم تسجيل المكالمات تلقائياً عند حدوثها بين الزبون والسائق
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((rec) => (
              <div
                key={rec.id}
                className="bg-white rounded-2xl p-4 talabya-shadow-soft border border-[var(--border)]"
                data-testid={`recording-row-${rec.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <PhoneCall size={14} className="text-primary-brand"/>
                      <span className="font-black text-sm">طلب {rec.order_code || rec.order_id.slice(0, 8)}</span>
                      <span className="text-xs text-[var(--text-secondary)]">
                        · {formatDate(rec.created_at)}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                      <span className="flex items-center gap-1">
                        <User size={12} className="text-[var(--text-secondary)]"/>
                        الزبون: <b>{rec.customer?.name || "-"}</b>
                        {rec.customer?.phone && <span className="text-[var(--text-secondary)]" dir="ltr">({rec.customer.phone})</span>}
                      </span>
                      <span className="flex items-center gap-1">
                        <Bike size={12} className="text-[var(--text-secondary)]"/>
                        السائق: <b>{rec.driver?.name || "-"}</b>
                        {rec.driver?.phone && <span className="text-[var(--text-secondary)]" dir="ltr">({rec.driver.phone})</span>}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} className="text-[var(--text-secondary)]"/>
                        <b>{formatDuration(rec.duration_sec)}</b>
                      </span>
                      <span className="text-[var(--text-secondary)]">{formatSize(rec.size_bytes)}</span>
                      <span className="text-[var(--text-secondary)]">رافع: {rec.uploader_role === "customer" ? "الزبون" : "السائق"}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => download(rec)}
                      className="p-2 rounded-lg bg-primary-light text-primary-brand hover:bg-blue-100"
                      aria-label="تنزيل"
                      data-testid={`download-${rec.id}`}
                    >
                      <Download size={16}/>
                    </button>
                    <button
                      onClick={() => remove(rec)}
                      className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
                      aria-label="حذف"
                      data-testid={`delete-${rec.id}`}
                    >
                      <Trash2 size={16}/>
                    </button>
                  </div>
                </div>

                {audioUrls[rec.id] ? (
                  <audio
                    src={audioUrls[rec.id]}
                    controls
                    className="w-full"
                    data-testid={`player-${rec.id}`}
                  />
                ) : (
                  <button
                    onClick={() => loadAudio(rec)}
                    className="w-full py-2 rounded-lg bg-[var(--surface-alt)] hover:bg-blue-50 text-sm font-bold text-primary-brand"
                    data-testid={`load-player-${rec.id}`}
                  >
                    ▶ تشغيل التسجيل
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
